import { Vehicle } from '@parkingbase/ecommerce-api-test';
import { VehicleService } from '@/services/vehicle.service';

import { create } from 'zustand';
import { logger } from '@/lib/logger';
import { extractApiErrorMessage } from '@/lib/api-error-utils';

interface VehicleState {
  vehicles: Vehicle[];
  isLoading: boolean;
  /**
   * Backend error message from the most recent create/update failure, or null.
   * Additive surface (PB-1712): the create/update contract still returns
   * `Vehicle | null`, so existing callers (touchless/reserve/QR/extend) are
   * unaffected — consumers that want to show the real message read this.
   */
  error: string | null;
  clearVehicleError: () => void;
  getAllVehicles: () => Promise<void>;
  setVehicles: (vehicles: Vehicle[]) => void;
  reset: () => void;
  getDefaultVehicle: () => Vehicle | null;
  createVehicle: (vehicle: Vehicle) => Promise<Vehicle | null>;
  updateVehicle: (vehicle: Vehicle) => Promise<Vehicle | null>;
  deleteVehicle: (id: string) => Promise<boolean>;
}

export const useVehicleStore = create<VehicleState>((set, get) => ({
  vehicles: [],
  isLoading: false,
  error: null,
  clearVehicleError: () => set({ error: null }),
  setVehicles: (vehicles: Vehicle[]) => set({ vehicles }),
  reset: () => set({ vehicles: [], isLoading: false, error: null }),
  getDefaultVehicle: () => {
    const vehicles = get().vehicles;
    return (
      (vehicles || []).find((vehicle) => vehicle.defaultVehicle) ||
      (vehicles?.length > 0 ? vehicles[0] : null)
    );
  },
  getAllVehicles: async () => {
    try {
      set({ isLoading: true });
      const vehicleService = new VehicleService();
      const vehicles = await vehicleService.getUserVehicles();
      if (vehicles?.length > 0) {
        vehicles.sort((a, b) => (a.defaultVehicle ? -1 : 1));
      }
      set({ vehicles });
    } catch (error) {
      logger.error('Failed to fetch user vehicles', error, {
        feature: 'vehicle-store',
        route: 'getAllVehicles',
      });
    } finally {
      set({ isLoading: false });
    }
  },
  createVehicle: async (vehicle: Vehicle) => {
    try {
      set({ isLoading: true, error: null });
      const vehicleService = new VehicleService();
      const newVehicle = await vehicleService.createVehicle(vehicle);
      if (newVehicle) {
        await get().getAllVehicles();
      }
      return newVehicle;
    } catch (error) {
      logger.error('Failed to create vehicle', error, {
        feature: 'vehicle-store',
        route: 'createVehicle',
      });
      set({ error: extractApiErrorMessage(error, '') || null });
      return null;
    } finally {
      set({ isLoading: false });
    }
  },
  updateVehicle: async (vehicle: Vehicle) => {
    try {
      set({ isLoading: true, error: null });
      const vehicleService = new VehicleService();
      const updatedVehicle = await vehicleService.updateVehicle(vehicle);
      if (updatedVehicle) {
        await get().getAllVehicles();
      }
      return updatedVehicle;
    } catch (error) {
      logger.error('Failed to update vehicle', error, {
        feature: 'vehicle-store',
        route: 'updateVehicle',
      });
      set({ error: extractApiErrorMessage(error, '') || null });
      return null;
    } finally {
      set({ isLoading: false });
    }
  },
  deleteVehicle: async (id: string) => {
    try {
      set({ isLoading: true });
      const vehicleService = new VehicleService();
      await vehicleService.deleteVehicle(id);
      await get().getAllVehicles();
      return true;
    } catch (error) {
      logger.error('Failed to delete vehicle', error, {
        feature: 'vehicle-store',
        route: 'deleteVehicle',
        id,
      });
      return false;
    } finally {
      set({ isLoading: false });
    }
  },
}));
